import React, { useEffect, useState } from "react";
import { Alert, Container } from "../../components";
import { Accordion, Icon, Text, Heading } from "@telia/teddy";
import _map from "lodash/map";
import { connect } from "react-redux";
import { ClientSideConfig, ContentfulQueueElement, RootState } from "../../../../models";
import LoadingPebble from "../../components/components/styleguide/LoadingPebble/LoadingPebble";
import Loading from "../components/atoms/Loading";
import { waitInQueue, updateHeartBeat } from "../RapidQueue";
import { getQueueElements } from "../state/selectors/queueDataSelector";

interface WaitingInQueuePageContentProps {
    maxPeopleInside?: number;
    peopleInQueueOnLoad?: number;
    placeInQueue?: number;
    queueElements: ContentfulQueueElement[] | undefined;
}

const WaitingInQueuePageContent: React.FC<WaitingInQueuePageContentProps> = ({
    placeInQueue,
    peopleInQueueOnLoad,
    queueElements,
    maxPeopleInside,
}) => (
    <Container
        style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}
        size="medium"
        noPadding
        extraPaddingTop
    >
        <Heading mb="100" as="h1" variant="title-600">
            {"Kø i kassa"}
        </Heading>
        <Text mb="100" className="queue-page__info-text">
            Det er stor pågang akkurat nå. Vennligst bli værende på siden - du blir automatisk tatt videre når det blir
            din tur.
        </Text>
        {placeInQueue && peopleInQueueOnLoad && <LoadingPebble className="queue-page__loading-pebble" />}
        <Alert kind="info" className="queue-page--alert">
            Ikke forlat denne siden. Da vil du miste din plass i køen
        </Alert>
        <br />
        <br />
        {placeInQueue && maxPeopleInside && (
            <Heading as="h2" variant="title-100">
                Du er nummer <span className="queue-page--purple">{Math.ceil(placeInQueue / maxPeopleInside)}</span> i
                køen
            </Heading>
        )}

        <div className="queue-page__accordion-container">
            <div className="queue-page__sub-container">
                {_map(queueElements, (queueElement, queueElementIndex) => {
                    const queueElementKey = queueElement.heading ?? `queue-element-${queueElementIndex}`;
                    const queueHeroUrl = queueElement.queueHero?.file.url;

                    return (
                        <React.Fragment key={queueElementKey}>
                            {queueHeroUrl && (
                                <img
                                    className="queue-page__hero-image"
                                    src={`http:${queueHeroUrl}`}
                                    alt={queueElement.queueHero?.title || ""}
                                />
                            )}
                            <div className="queue-page__sub-header">
                                {queueElement.headingIcon && (
                                    <Icon className="queue-page__sub-header-icon" name={queueElement.headingIcon} />
                                )}
                                <span className="queue-page__sub-header-text">{queueElement.heading}</span>
                            </div>
                            <Accordion type="multiple">
                                {_map(queueElement.expandableBlocks, (block, index) => (
                                    <Accordion.Item
                                        key={block.id ?? block.heading ?? `queue-block-${index}`}
                                        value={block.id ?? block.heading ?? `queue-block-${index}`}
                                    >
                                        <Accordion.Header>
                                            <Accordion.Trigger className="queue-page__accordion-trigger">
                                                <span className="queue-page__accordion-trigger-content">
                                                    {block.headingIcon && <Icon name={block.headingIcon} size="md" />}
                                                    {block.heading || ""}
                                                </span>
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <Accordion.Content>
                                            <div
                                                className="queue-page__accordion-item"
                                                dangerouslySetInnerHTML={{ __html: block.content }}
                                            />
                                        </Accordion.Content>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                            <div className="queue-page__footers-container">
                                {queueElement.footers?.map((footer) => (
                                    <div className="queue-page__accordion-footer" key={footer.id}>
                                        <img
                                            className="queue-page__footer-image"
                                            src={footer.image.file.url}
                                            alt={footer.image.title}
                                        />
                                    </div>
                                ))}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    </Container>
);

interface WaitingInQueuePageProps extends WaitingInQueuePageContentProps {
    loading: boolean;
    maxPeopleInside?: number;
}

const WaitingInQueuePage: React.FC<WaitingInQueuePageProps> = ({
    loading,
    placeInQueue,
    peopleInQueueOnLoad,
    queueElements,
    maxPeopleInside,
}) => (
    <div className="content">
        <div className="queue-page">
            {loading || !(placeInQueue && peopleInQueueOnLoad) ? (
                <Loading />
            ) : (
                <div>
                    <WaitingInQueuePageContent
                        placeInQueue={placeInQueue}
                        peopleInQueueOnLoad={peopleInQueueOnLoad}
                        queueElements={queueElements}
                        maxPeopleInside={maxPeopleInside}
                    />
                </div>
            )}
        </div>
    </div>
);

interface WaitQueuePageRootStateProps {
    config: ClientSideConfig;
    orderRef: string;
    queueElements: ContentfulQueueElement[] | undefined;
}

interface WaitQueuePageRootProps extends WaitQueuePageRootStateProps {
    children: React.ReactNode;
}

const WaitQueuePageRoot: React.FC<WaitQueuePageRootProps> = ({ orderRef, config, children, queueElements }) => {
    const [waitingInQueue, setWaitingInQueue] = useState(true);
    const [loading, setLoading] = useState(true);
    const [placeInQueue, setPlaceInQueue] = useState<number>();
    const [peopleInQueueOnLoad, setPeopleInQueueOnLoad] = useState<number>();
    const [maxPeopleInside, setMaxPeopleInside] = useState<number | undefined>();
    let interval: ReturnType<typeof setInterval> | undefined;

    useEffect(() => {
        const { checkoutQueueConnection } = config;

        waitInQueue(orderRef, checkoutQueueConnection, {
            onIsPutInQueue: () => {
                // update heartbeat evry minute so it's possible to remove people who has no heartbeat efter 5 min
                updateHeartBeat(orderRef);
                interval = setInterval(() => {
                    updateHeartBeat(orderRef);
                }, 1000 * 30);
            },
            onIsInside: (isInside: boolean) => {
                clearInterval(interval);
                setWaitingInQueue(!isInside);
            },
            onIsLoading: (value: boolean) => setLoading(value),
            onPlaceInQueueUpdate: ({ placeInQueue: piq, maxPeopleInside: newMaxPeopleInside }) => {
                setPlaceInQueue(piq);
                if (!peopleInQueueOnLoad) {
                    setPeopleInQueueOnLoad(piq);
                }
                setMaxPeopleInside(newMaxPeopleInside);
            },
        });

        return () => {
            clearInterval(interval);
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    if (waitingInQueue) {
        return (
            <div>
                <WaitingInQueuePage
                    loading={loading}
                    placeInQueue={placeInQueue}
                    peopleInQueueOnLoad={peopleInQueueOnLoad}
                    queueElements={queueElements}
                    maxPeopleInside={maxPeopleInside}
                />
            </div>
        );
    }

    return <>{children}</>;
};

const mapStateToProps = (state: RootState): WaitQueuePageRootStateProps => ({
    orderRef: state.general.orderRef || "",
    config: state.config,
    queueElements: getQueueElements(state),
});

export default connect(mapStateToProps)(WaitQueuePageRoot);
